import { useState, type JSX } from 'react'

import { Button } from '../design-system/button.tsx'
import { Input } from '../design-system/input.tsx'
import { Select } from '../design-system/select.tsx'
import { fieldWords } from '../lib/alert-chips.ts'
import { filterInput, filterValue } from '../lib/alert-filter.ts'
import type {
  AlertFilterField,
  AlertReportWords
} from '../lib/alert-rules-api.ts'

/**
 * Add one column filter to the view an alert watches: a field, an operator and a value.
 *
 * The report's catalogue entry lists the fields and operators, so the sheet
 * offers exactly the filters the page's own table takes.
 *
 * @param props - Control props.
 * @param props.fields - The report's filterable fields.
 * @param props.words - The report's words, for field and operator names.
 * @param props.onAdd - Adds the filter, as its URL parameter and value.
 * @returns The control.
 */
export function AlertFilterAdd({
  fields,
  words,
  onAdd
}: {
  fields: readonly AlertFilterField[]
  words: AlertReportWords
  onAdd: (param: string, value: string) => void
}): JSX.Element | null {
  const [name, setName] = useState(fields[0]?.name ?? '')
  const field = fields.find(item => item.name === name) ?? fields[0]
  const [op, setOp] = useState(field?.operators[0] ?? '')
  const [first, setFirst] = useState('')
  const [second, setSecond] = useState('')
  if (!field) {
    return null
  }
  const operator = field.operators.includes(op)
    ? op
    : (field.operators[0] ?? '')
  const input = filterInput(field, operator)
  const value =
    input.kind === 'choice'
      ? filterValue(input, first || (input.options[0]?.value ?? ''))
      : filterValue(input, first, second)
  const reset = () => {
    setFirst('')
    setSecond('')
  }

  return (
    <fieldset
      data-testid="alert-sheet-add-filter"
      className="grid gap-2 border-t border-border pt-3"
    >
      <legend className="text-sm font-medium">Add a filter</legend>
      <Select
        data-testid="alert-sheet-filter-field"
        label="Column"
        options={fields.map(item => ({
          value: item.name,
          label: fieldWords(item.name, words)
        }))}
        value={field.name}
        onChange={event => {
          setName(event.target.value)
          setOp('')
          reset()
        }}
      />
      <Select
        data-testid="alert-sheet-filter-op"
        label="Condition"
        options={field.operators.map(item => ({
          value: item,
          label:
            item === 'isnull'
              ? 'is empty or not'
              : (words.operators[item] ?? item)
        }))}
        value={operator}
        onChange={event => {
          setOp(event.target.value)
          reset()
        }}
      />
      {input.kind === 'choice' ? (
        <Select
          data-testid="alert-sheet-filter-value"
          label="Value"
          options={input.options}
          value={first || (input.options[0]?.value ?? '')}
          onChange={event => setFirst(event.target.value)}
        />
      ) : (
        <Input
          data-testid="alert-sheet-filter-value"
          label={input.kind === 'range' ? 'From' : 'Value'}
          type={input.type}
          value={first}
          hint={input.kind === 'text' ? input.hint : undefined}
          onChange={event => setFirst(event.target.value)}
        />
      )}
      {input.kind === 'range' ? (
        <Input
          data-testid="alert-sheet-filter-value-to"
          label="To"
          type={input.type}
          value={second}
          onChange={event => setSecond(event.target.value)}
        />
      ) : null}
      <div>
        <Button
          data-testid="alert-sheet-filter-add"
          variant="outline"
          disabled={value === null}
          onClick={() => {
            if (value !== null) {
              onAdd(`${field.name}__${operator}`, value)
              reset()
            }
          }}
        >
          Add filter
        </Button>
      </div>
    </fieldset>
  )
}
